import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Grid3x3, Scissors, Percent, Ruler, ExternalLink, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import BoardMiniCanvas from './BoardMiniCanvas';

const CuttingPlanResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [plan, setPlan] = useState(null);
  const [boards, setBoards] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);

  useEffect(() => {
    if (!canAccess) {
      setError('Você não tem permissão');
      setLoading(false);
      return;
    }
    loadData();
  }, [id, canAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [planRes, boardsRes, posRes] = await Promise.all([
        cuttingPlanService.getPlanById(id),
        cuttingPlanService.getBoardsByPlan(id),
        cuttingPlanService.getPositionsByPlan(id),
      ]);

      if (!planRes.success) {
        setError('Plano não encontrado');
        return;
      }
      setPlan(planRes.data);
      setBoards(boardsRes.success ? boardsRes.data : []);
      setPositions(posRes.success ? posRes.data : []);
    } catch (err) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getPositionsByBoard = (boardId) =>
    positions.filter((p) => p.board_id === boardId);

  // Agrupar chapas idênticas para o operador cortar de uma só vez (padrão Corte Certo)
  const groupedBoards = useMemo(() => {
    if (!boards || boards.length === 0) return [];
    const groups = [];

    boards.forEach((board) => {
      const bPositions = positions.filter((p) => p.board_id === board.id);
      
      // Cria uma assinatura geométrica única do layout de corte das peças
      const signature = bPositions
        .map(p => `${Math.round(p.position_x || p.x || 0)}:${Math.round(p.position_y || p.y || 0)}:${Math.round(p.part_width || p.width || 0)}x${Math.round(p.part_height || p.height || 0)}`)
        .sort()
        .join('|');

      const fullSignature = `${board.width}x${board.height}-${board.material_name || ''}-${signature}`;

      const existingGroup = groups.find(g => g.signature === fullSignature && signature.length > 0);
      if (existingGroup) {
        existingGroup.count += 1;
        existingGroup.boardNumbers.push(board.board_number);
        existingGroup.allBoards.push(board);
      } else {
        groups.push({
          signature: fullSignature,
          count: 1,
          primaryBoard: board,
          boardNumbers: [board.board_number],
          allBoards: [board]
        });
      }
    });

    return groups;
  }, [boards, positions]);

  const getBoardEfficiency = (board) => {
    if (!board) return 0;
    const bw = parseFloat(board.width) || 2750;
    const bh = parseFloat(board.height) || 1840;
    const totalArea = (bw * bh) / 1000000;
    if (totalArea <= 0) return 0;
    const boardPositions = getPositionsByBoard(board.id);
    const used = boardPositions.reduce((sum, p) => {
      const pw = parseFloat(p.part_width || p.width || 0);
      const ph = parseFloat(p.part_height || p.height || 0);
      return sum + (pw * ph) / 1000000;
    }, 0);
    return ((used / totalArea) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded" />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-medium">{error || 'Plano não encontrado'}</p>
          <button onClick={() => navigate('/cutting-plans')} className="text-red-600 hover:text-red-800 text-sm mt-2">
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  const totalArea = boards.reduce((sum, b) => {
    const bw = parseFloat(b.width) || 2750;
    const bh = parseFloat(b.height) || 1840;
    return sum + (bw * bh) / 1000000;
  }, 0);
  const totalPositions = positions.length;
  const usedArea = positions.reduce((sum, p) => {
    const pw = parseFloat(p.part_width || p.width || 0);
    const ph = parseFloat(p.part_height || p.height || 0);
    return sum + (pw * ph) / 1000000;
  }, 0);
  const globalEff = totalArea > 0 ? ((usedArea / totalArea) * 100).toFixed(1) : 0;

  return (
    <div className="p-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        <button onClick={() => navigate('/cutting-plans')} className="flex items-center gap-1 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Planos de Corte
        </button>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        Resultado — {plan.plan_code}
      </h1>
      <p className="text-gray-500 mb-6">{plan.description || 'Sem descrição'}</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg"><Grid3x3 className="w-6 h-6 text-blue-600" /></div>
            <div>
              <p className="text-sm text-gray-600">Chapas</p>
              <p className="text-2xl font-bold text-gray-900">{boards.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg"><Scissors className="w-6 h-6 text-green-600" /></div>
            <div>
              <p className="text-sm text-gray-600">Peças</p>
              <p className="text-2xl font-bold text-gray-900">{totalPositions}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg"><Ruler className="w-6 h-6 text-purple-600" /></div>
            <div>
              <p className="text-sm text-gray-600">Área Total</p>
              <p className="text-2xl font-bold text-gray-900">{totalArea.toFixed(2)} m²</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-100 rounded-lg"><Percent className="w-6 h-6 text-yellow-600" /></div>
            <div>
              <p className="text-sm text-gray-600">Eficiência</p>
              <p className="text-2xl font-bold text-gray-900">{globalEff}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Board list */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Chapas ({boards.length} no total • {groupedBoards.length} layouts únicos)
        </h2>
        {groupedBoards.some(g => g.count > 1) && (
          <span className="text-xs font-semibold px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg">
            ⚡ Contém chapas idênticas para corte simultâneo
          </span>
        )}
      </div>

      {boards.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Grid3x3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhuma chapa gerada</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groupedBoards.map((group, gIdx) => {
            const board = group.primaryBoard;
            const boardPositions = getPositionsByBoard(board.id);
            const eff = getBoardEfficiency(board);
            const hasMultiple = group.count > 1;

            return (
              <div
                key={group.signature || board.id}
                className={`bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow relative ${hasMultiple ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900">
                        {hasMultiple
                          ? `Plano #${gIdx + 1} (Chapas: ${group.boardNumbers.map(n => `#${n}`).join(', ')})`
                          : `Chapa #${board.board_number}`}
                      </h3>
                      {hasMultiple && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-black bg-amber-500 text-white shadow-xs">
                          Qtd: {group.count} chapas iguais
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {parseFloat(board.width) || 2750} x {parseFloat(board.height) || 1840} mm
                      {board.material_name ? ` — ${board.material_name}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900">{eff}%</span>
                    <p className="text-xs text-gray-500">eficiência</p>
                  </div>
                </div>

                <div className="flex justify-center mb-3">
                  <BoardMiniCanvas
                    board={board}
                    positions={boardPositions}
                    compact={false}
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-3 bg-gray-50 p-2 rounded-lg">
                  <span>{boardPositions.length} peças nesta chapa</span>
                  <span>
                    Total: {boardPositions.length * group.count} peças ({group.count}x)
                  </span>
                </div>

                <button
                  onClick={() => navigate(`/cutting-plans/${id}/boards/${board.id}`)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Visualizar Detalhes do Corte
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={() => navigate(`/cutting-plans/${id}`)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Detalhes do Plano
        </button>
        <button
          onClick={() => navigate(`/cutting-plans/${id}/edit`)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Editar Plano
        </button>
      </div>
    </div>
  );
};

export default CuttingPlanResult;
